import {PageStandard} from "../utils/PageStandard";
import {
    Button,
    Card,
    Chip,
    Drawer,
    FormControl,
    FormLabel,
    IconButton,
    Input,
    Option,
    Select,
    Sheet,
    Stack,
    Typography,
} from "@mui/joy";
import React, {useEffect, useState} from "react";
import {CategoriaModel, ServizioModel, UserModel,} from "../utils/Model";
import {Add, Delete, DeleteForever, Edit, FileDownload} from "@mui/icons-material";
import {addService, deleteService, editServizio, getCategories, getServices} from "../utils/ViewModel";
import {MapContainer, Marker, TileLayer, useMapEvents} from "react-leaflet";
import {LatLng} from "leaflet";
import {downloadCSV, hasChanges} from "../utils/Utils";
import moment from "moment/moment";

export function Servizi() {
    const [modalAddServizio, setModalAddServizio] = useState(false);

    const initialDataNewServizio: Omit<ServizioModel, "id"> = {
        categoryId: "",
        title: "",
        coordinates: {
            lat: 0,
            lng: 0,
        },
        address: "",
        phoneNumbers: [],
        openingHours: [],
        notes: "",
        booking: "",
        website: [],
        emailAddress: [],
    };
    const [newServizio, setNewServizio] = useState(initialDataNewServizio);

    const [servizi, setServizi] = useState<ServizioModel[]>([]);
    const [categorie, setCategorie] = useState<CategoriaModel[]>([]);

    useEffect(() => {
        loadData();
    }, []);

    function loadData() {
        getCategories().then((data) => {
            setCategorie(data);
        });
        getServices().then((data) => {
            setServizi(data);
        });
    }

    // Aggiorna proprietà di newServizio in modo generico
    function handleChange<T extends keyof Omit<ServizioModel, "id">>(
        key: T,
        value: Omit<ServizioModel, "id">[T]
    ) {
        setNewServizio((old) => ({...old, [key]: value}));
    }

    // Gestione array generica (aggiungi, modifica, elimina)
    function handleArrayChange(
        key: keyof Omit<ServizioModel, "id">,
        index: number,
        value: string
    ) {
        const arr = newServizio[key] as string[] | undefined;
        if (!arr) return;
        const newArr = [...arr];
        newArr[index] = value;
        handleChange(key, newArr as any);
    }

    function addArrayItem(key: keyof Omit<ServizioModel, "id">) {
        const arr = newServizio[key] as string[] | undefined;
        const newArr = arr ? [...arr, ""] : [""];
        handleChange(key, newArr as any);
    }

    function removeArrayItem(key: keyof Omit<ServizioModel, "id">, index: number) {
        const arr = newServizio[key] as string[] | undefined;
        if (!arr) return;
        const newArr = arr.filter((_, i) => i !== index);
        handleChange(key, newArr as any);
    }

    const handleSubmit = () => {
        // Validazioni base coordinate rimangono da fare
        addService(newServizio)
            .then(() => {
                setModalAddServizio(false);
                setNewServizio(initialDataNewServizio);
                loadData();
            })
            .catch(() => {
                // gestione errore già fatto in ViewModel con alert
            });
    };

    function LocationMarker() {
        const map = useMapEvents({
            click(e) {
                setNewServizio({...newServizio, coordinates: {lat: e.latlng.lat, lng: e.latlng.lng}})
                map.flyTo(e.latlng, map.getZoom())
            },
        })

        return (newServizio.coordinates.lat === null || newServizio.coordinates.lng === null) ? null : (
            <Marker position={new LatLng(newServizio.coordinates.lat, newServizio.coordinates.lng)} />
        )
    }

    return (
        <PageStandard
            title="Servizi"
            sidebarVisible={true}
            headerEnd={
            <Stack direction="row" spacing={2}>
                <Button onClick={() => setModalAddServizio(true)}>Aggiungi servizio</Button>
                <Button startDecorator={<FileDownload />}
                        onClick={() => {
                            downloadCSV<ServizioModel>({
                                data: servizi,
                                fields: ["id", "categoryId", "title", "coordinates.lat", "coordinates.lng", "address", "phoneNumbers", "openingHours", "notes", "booking", "website", "emailAddress"],
                                filename: `oasi-servizi-${moment().format("YYYY-MM-DD")}.csv`,
                            })
                        }}
                >
                    Esporta tutti in CSV
                </Button>
            </Stack>
            }
        >
            <Stack direction="column" spacing={2} pb={5}>
                {servizi?.length > 0 ? (
                    servizi.map((c) => (
                        <ItemServizio
                            key={c.id}
                            data={c}
                            categorie={categorie}
                            loadData={loadData}
                        />
                    ))
                ) : (
                    <Typography level="body-md">Non sono presenti servizi</Typography>
                )}
            </Stack>

            <Drawer open={modalAddServizio} onClose={() => setModalAddServizio(false)}
                    anchor="right"
                    size="md"
                    variant="plain"
                    slotProps={{
                        content: {
                            sx: {
                                bgcolor: 'transparent',
                                p: {md: 3, sm: 0},
                                boxShadow: 'none',
                                minWidth: "700px"
                            },
                        },
                    }}
            >
                <Sheet
                    sx={{
                        borderRadius: 'xl',
                        p: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        height: '100%',
                        overflow: 'auto',
                    }}
                >
                    <Stack direction="column" spacing={2} p={3}>
                        <Typography component="h2" level="h4" sx={{fontWeight: "lg", mb: 1}}>
                            Aggiungi servizio
                        </Typography>
                        <FormControl>
                            <FormLabel>Categoria</FormLabel>
                            <Select
                                value={newServizio.categoryId}
                                onChange={(e, newVal) => handleChange("categoryId", newVal!)}
                            >
                                <Option value="" disabled>
                                    Scegli
                                </Option>
                                {categorie.map((c) => (
                                    <Option value={c.id} key={c.id}>
                                        {c.icon} {c.title}
                                    </Option>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl>
                            <FormLabel>Titolo servizio</FormLabel>
                            <Input
                                type="text"
                                value={newServizio.title}
                                onChange={(e) => handleChange("title", e.target.value)}
                                placeholder="Titolo servizio"
                            />
                        </FormControl>

                        {/* Location */}
                        <FormControl>
                            <FormLabel>Posizione</FormLabel>
                            <div style={{height: '400px'}}>
                                <MapContainer center={[45.07049, 7.68682]} zoom={16} scrollWheelZoom={true}
                                              className="map-container-leaflet">
                                    <TileLayer
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    />
                                    <LocationMarker/>
                                </MapContainer>
                            </div>
                        </FormControl>

                        <FormControl>
                            <FormLabel>Indirizzo</FormLabel>
                            <Input
                                type="text"
                                value={newServizio.address ?? ""}
                                onChange={(e) => handleChange("address", e.target.value)}
                                placeholder="Via Roma 1"
                            />
                        </FormControl>

                        {/* Phone Numbers */}
                        <FormControl>
                            <FormLabel>
                            Numeri di telefono
                                <IconButton
                                    size="sm"
                                    variant="outlined"
                                    sx={{ml: 1}}
                                    onClick={() => addArrayItem("phoneNumbers")}
                                >
                                    <Add/>
                                </IconButton>
                            </FormLabel>
                            {(newServizio.phoneNumbers || []).map((num, index) => (
                                <Input
                                    key={index}
                                    type="text"
                                    placeholder="333 000 0000"
                                    value={num}
                                    onChange={(e) => handleArrayChange("phoneNumbers", index, e.target.value)}
                                    endDecorator={
                                        <IconButton
                                            onClick={() => removeArrayItem("phoneNumbers", index)}
                                            disabled={(newServizio.phoneNumbers?.length ?? 0) === 0}
                                        >
                                            <Delete/>
                                        </IconButton>
                                    }
                                />
                            ))}
                        </FormControl>

                        {/* Opening Hours */}
                        <FormControl>
                            <FormLabel>
                                Orari di apertura
                                <IconButton
                                    size="sm"
                                    variant="outlined"
                                    sx={{ml: 1}}
                                    onClick={() => addArrayItem("openingHours")}
                                >
                                    <Add/>
                                </IconButton>
                            </FormLabel>
                            {(newServizio.openingHours || []).map((o, index) => (
                                <Input
                                    key={index}
                                    type="text"
                                    placeholder="Lun-Ven 9:00 - 18:00"
                                    value={o}
                                    onChange={(e) => handleArrayChange("openingHours", index, e.target.value)}
                                    endDecorator={
                                        <IconButton
                                            onClick={() => removeArrayItem("openingHours", index)}
                                            disabled={(newServizio.openingHours?.length ?? 0) === 0}
                                        >
                                            <Delete/>
                                        </IconButton>
                                    }
                                />
                            ))}
                        </FormControl>

                        {/* Notes */}
                        <FormControl>
                            <FormLabel>Note</FormLabel>
                            <Input
                                type="text"
                                value={newServizio.notes ?? ""}
                                onChange={(e) => handleChange("notes", e.target.value)}
                                placeholder="Note aggiuntive"
                            />
                        </FormControl>

                        {/* Booking */}
                        <FormControl>
                            <FormLabel>Prenotazione</FormLabel>
                            <Input
                                type="text"
                                value={newServizio.booking ?? ""}
                                onChange={(e) => handleChange("booking", e.target.value)}
                                placeholder="Modalità di prenotazione"
                            />
                        </FormControl>

                        {/* Website */}
                        <FormControl>
                            <FormLabel>
                                Siti web
                                <IconButton
                                    size="sm"
                                    variant="outlined"
                                    sx={{ml: 1}}
                                    onClick={() => addArrayItem("website")}
                                >
                                    <Add/>
                                </IconButton>
                            </FormLabel>
                            {(newServizio.website || []).map((w, index) => (
                                <Input
                                    key={index}
                                    type="text"
                                    placeholder="https://example.com"
                                    value={w}
                                    onChange={(e) => handleArrayChange("website", index, e.target.value)}
                                    endDecorator={
                                        <IconButton
                                            onClick={() => removeArrayItem("website", index)}
                                            disabled={(newServizio.website?.length ?? 0) === 0}
                                        >
                                            <Delete/>
                                        </IconButton>
                                    }
                                />
                            ))}
                        </FormControl>

                        {/* Email Address */}
                        <FormControl>
                            <FormLabel>
                                Email
                                <IconButton
                                    size="sm"
                                    variant="outlined"
                                    sx={{ml: 1}}
                                    onClick={() => addArrayItem("emailAddress")}
                                >
                                    <Add/>
                                </IconButton>
                            </FormLabel>
                            {(newServizio.emailAddress || []).map((e, index) => (
                                <Input
                                    key={index}
                                    type="email"
                                    placeholder="esempio@email.it"
                                    value={e}
                                    onChange={(evt) => handleArrayChange("emailAddress", index, evt.target.value)}
                                    endDecorator={
                                        <IconButton
                                            onClick={() => removeArrayItem("emailAddress", index)}
                                            disabled={(newServizio.emailAddress?.length ?? 0) === 0}
                                        >
                                            <Delete/>
                                        </IconButton>
                                    }
                                />
                            ))}
                        </FormControl>

                        <Button
                            fullWidth
                            disabled={
                                newServizio.categoryId.trim().length === 0 ||
                                newServizio.title.trim().length === 0 ||
                                newServizio.coordinates.lat === 0 ||
                                newServizio.coordinates.lng === 0
                            }
                            onClick={handleSubmit}
                        >
                            Aggiungi
                        </Button>
                    </Stack>
                </Sheet>
            </Drawer>
        </PageStandard>
    );
}

function ItemServizio(props: {
    data: ServizioModel;
    categorie: CategoriaModel[];
    loadData: () => void;
}) {
    const categorie = props.categorie;
    const loadData = props.loadData;
    const defaultdata = props.data

    const [data, setData] = useState<ServizioModel>(props.data);
    const [modalEdit, setModalEdit] = useState(false)

    const categoria = categorie.find((c) => c.id === defaultdata.categoryId);

    // Aggiorna proprietà di newServizio in modo generico
    function handleChange<T extends keyof ServizioModel>(
        key: T,
        value: ServizioModel[T]
    ) {
        setData((old) => ({...old, [key]: value}));
    }

    // Gestione array generica (aggiungi, modifica, elimina)
    function handleArrayChange(
        key: keyof ServizioModel,
        index: number,
        value: string
    ) {
        const arr = data[key] as string[] | undefined;
        if (!arr) return;
        const newArr = [...arr];
        newArr[index] = value;
        handleChange(key, newArr as any);
    }

    function addArrayItem(key: keyof ServizioModel) {
        const arr = data[key] as string[] | undefined;
        const newArr = arr ? [...arr, ""] : [""];
        handleChange(key, newArr as any);
    }

    function removeArrayItem(key: keyof ServizioModel, index: number) {
        const arr = data[key] as string[] | undefined;
        if (!arr) return;
        const newArr = arr.filter((_, i) => i !== index);
        handleChange(key, newArr as any);
    }

    const handleSubmit = () => {
        editServizio(data)
            .then(() => {
                setModalEdit(false);
                loadData();
            })
    };

    function LocationMarker() {
        const map = useMapEvents({
            click(e) {
                setData({...data, coordinates: {lat: e.latlng.lat, lng: e.latlng.lng}})
                map.flyTo(e.latlng, map.getZoom())
            },
        })

        return (data.coordinates.lat === null || data.coordinates.lng === null) ? null : (
            <Marker position={new LatLng(data.coordinates.lat, data.coordinates.lng)} />
        )
    }

    return (
        <Card>
            <Stack direction="row" spacing={2} justifyContent="space-between">
                <Stack direction="column" spacing={1} justifyContent="center">
                    <Typography level="title-md" fontWeight="bold">
                        {defaultdata.title}
                    </Typography>
                    <Typography level="body-sm">{defaultdata.address ?? ""}</Typography>
                    <Typography level="body-sm">{defaultdata.id}</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                    {categoria ? (
                        <Chip size="sm" color="neutral" variant="outlined">
                            <Typography level="body-sm" fontWeight="bold">
                                {categoria.icon} {categoria.title}
                            </Typography>
                        </Chip>
                    ) : (
                        <Chip size="sm" color="neutral" variant="soft">
                            <Typography level="body-sm" fontWeight="bold">
                                Nessuna categoria
                            </Typography>
                        </Chip>
                    )}
                    <IconButton
                        color="primary"
                        variant="outlined"
                        onClick={() => setModalEdit(true)}
                    >
                        <Edit/>
                    </IconButton>
                    <IconButton
                        color="danger"
                        variant="outlined"
                        onClick={() => {
                            if (
                                window.confirm(`Eliminare definitivamente il servizio ${defaultdata.title} ?`)
                            ) {
                                deleteService(defaultdata.id).then(() => {
                                    loadData();
                                });
                            }
                        }}
                    >
                        <DeleteForever/>
                    </IconButton>
                </Stack>
            </Stack>

            <Drawer open={modalEdit} onClose={() => cancelModal()}
                    anchor="right"
                    size="md"
                    variant="plain"
                    slotProps={{
                        content: {
                            sx: {
                                bgcolor: 'transparent',
                                p: {md: 3, sm: 0},
                                boxShadow: 'none',
                                minWidth: "700px"
                            },
                        },
                    }}
            >
                <Sheet
                    sx={{
                        borderRadius: 'xl',
                        p: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        height: '100%',
                        overflow: 'auto',
                    }}
                >
                    <Stack direction="column" spacing={2} p={3}>
                        <Typography component="h2" level="h4" sx={{fontWeight: "lg", mb: 1}}>
                            Modifica servizio
                        </Typography>
                        <FormControl>
                            <FormLabel>Categoria</FormLabel>
                            <Select
                                value={data.categoryId}
                                onChange={(e, newVal) => handleChange("categoryId", newVal!)}
                            >
                                <Option value="" disabled>
                                    Scegli
                                </Option>
                                {categorie.map((c) => (
                                    <Option value={c.id} key={c.id}>
                                        {c.icon} {c.title}
                                    </Option>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl>
                            <FormLabel>Titolo servizio</FormLabel>
                            <Input
                                type="text"
                                value={data.title}
                                onChange={(e) => handleChange("title", e.target.value)}
                                placeholder="Titolo servizio"
                            />
                        </FormControl>

                        {/* Location */}
                        <FormControl>
                            <FormLabel>Posizione</FormLabel>
                            <div style={{height: '400px'}}>
                                <MapContainer center={[45.07049, 7.68682]} zoom={16} scrollWheelZoom={true}
                                              className="map-container-leaflet">
                                    <TileLayer
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    />
                                    <LocationMarker/>
                                </MapContainer>
                            </div>
                        </FormControl>

                        <FormControl>
                            <FormLabel>Indirizzo</FormLabel>
                            <Input
                                type="text"
                                value={data.address ?? ""}
                                onChange={(e) => handleChange("address", e.target.value)}
                                placeholder="Via Roma 1"
                            />
                        </FormControl>

                        {/* Phone Numbers */}
                        <FormControl>
                            <FormLabel>
                                Numeri di telefono
                                <IconButton
                                    size="sm"
                                    variant="outlined"
                                    sx={{ml: 1}}
                                    onClick={() => addArrayItem("phoneNumbers")}
                                >
                                    <Add/>
                                </IconButton>
                            </FormLabel>
                            {(data.phoneNumbers || []).map((num, index) => (
                                <Input
                                    key={index}
                                    type="text"
                                    placeholder="333 000 0000"
                                    value={num}
                                    onChange={(e) => handleArrayChange("phoneNumbers", index, e.target.value)}
                                    endDecorator={
                                        <IconButton
                                            onClick={() => removeArrayItem("phoneNumbers", index)}
                                            disabled={(data.phoneNumbers?.length ?? 0) === 0}
                                        >
                                            <Delete/>
                                        </IconButton>
                                    }
                                />
                            ))}
                        </FormControl>

                        {/* Opening Hours */}
                        <FormControl>
                            <FormLabel>
                                Orari di apertura
                                <IconButton
                                    size="sm"
                                    variant="outlined"
                                    sx={{ml: 1}}
                                    onClick={() => addArrayItem("openingHours")}
                                >
                                    <Add/>
                                </IconButton>
                            </FormLabel>
                            {(data.openingHours || []).map((o, index) => (
                                <Input
                                    key={index}
                                    type="text"
                                    placeholder="Lun-Ven 9:00 - 18:00"
                                    value={o}
                                    onChange={(e) => handleArrayChange("openingHours", index, e.target.value)}
                                    endDecorator={
                                        <IconButton
                                            onClick={() => removeArrayItem("openingHours", index)}
                                            disabled={(data.openingHours?.length ?? 0) === 0}
                                        >
                                            <Delete/>
                                        </IconButton>
                                    }
                                />
                            ))}
                        </FormControl>

                        {/* Notes */}
                        <FormControl>
                            <FormLabel>Note</FormLabel>
                            <Input
                                type="text"
                                value={data.notes ?? ""}
                                onChange={(e) => handleChange("notes", e.target.value)}
                                placeholder="Note aggiuntive"
                            />
                        </FormControl>

                        {/* Booking */}
                        <FormControl>
                            <FormLabel>Prenotazione</FormLabel>
                            <Input
                                type="text"
                                value={data.booking ?? ""}
                                onChange={(e) => handleChange("booking", e.target.value)}
                                placeholder="Modalità di prenotazione"
                            />
                        </FormControl>

                        {/* Website */}
                        <FormControl>
                            <FormLabel>
                                Siti web
                                <IconButton
                                    size="sm"
                                    variant="outlined"
                                    sx={{ml: 1}}
                                    onClick={() => addArrayItem("website")}
                                >
                                    <Add/>
                                </IconButton>
                            </FormLabel>
                            {(data.website || []).map((w, index) => (
                                <Input
                                    key={index}
                                    type="text"
                                    placeholder="https://example.com"
                                    value={w}
                                    onChange={(e) => handleArrayChange("website", index, e.target.value)}
                                    endDecorator={
                                        <IconButton
                                            onClick={() => removeArrayItem("website", index)}
                                            disabled={(data.website?.length ?? 0) === 0}
                                        >
                                            <Delete/>
                                        </IconButton>
                                    }
                                />
                            ))}
                        </FormControl>

                        {/* Email Address */}
                        <FormControl>
                            <FormLabel>
                                Email
                                <IconButton
                                    size="sm"
                                    variant="outlined"
                                    sx={{ml: 1}}
                                    onClick={() => addArrayItem("emailAddress")}
                                >
                                    <Add/>
                                </IconButton>
                            </FormLabel>
                            {(data.emailAddress || []).map((e, index) => (
                                <Input
                                    key={index}
                                    type="email"
                                    placeholder="esempio@email.it"
                                    value={e}
                                    onChange={(evt) => handleArrayChange("emailAddress", index, evt.target.value)}
                                    endDecorator={
                                        <IconButton
                                            onClick={() => removeArrayItem("emailAddress", index)}
                                            disabled={(data.emailAddress?.length ?? 0) === 0}
                                        >
                                            <Delete/>
                                        </IconButton>
                                    }
                                />
                            ))}
                        </FormControl>

                        <Stack direction="row" spacing={2}>
                            <Button fullWidth
                                    variant="outlined"
                                    color="neutral"
                                    onClick={() => cancelModal()}
                            >
                                Annulla
                            </Button>
                            <Button
                                fullWidth
                                disabled={
                                    data.categoryId.trim().length === 0 ||
                                    data.title.trim().length === 0 ||
                                    data.coordinates.lat === 0 ||
                                    data.coordinates.lng === 0 ||
                                    !hasChanges(defaultdata, data)
                                }
                                onClick={handleSubmit}
                            >
                                Modifica
                            </Button>
                        </Stack>

                    </Stack>
                </Sheet>
            </Drawer>
        </Card>
    )

    function cancelModal() {
        setModalEdit(false)
        setData(defaultdata)
    }
}